import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import i18n from 'i18next';
import { css } from '@emotion/core';
import Typography from '@material-ui/core/Typography';

import * as clientErrTypes from 'config/clientErrorTypes';
import { IS_IFRAME_MODE, IFRAME_MODE_MIN_HEIGHT } from 'config/envVariable';
import { getUserPoints, exchangePoints, getKKboxUserPoints, exchangeKKboxPoints } from 'api/client';
import { setNeedReducePoint } from 'client/redux/modules/userEntryState';
import useClientErrorHandler, { generateApiErrObj } from 'client/hooks/useClientErrorHandler';

import useLogout from 'client/hooks/logout/useLogout';
import useLogoutRedirect from 'client/hooks/logout/useLogoutRedirect';

import PointJoin from 'client/components/dialogVerification/popupPayPoint/PointJoin';
import PointSuccess from 'client/components/dialogVerification/popupPayPoint/PointSuccess';
import ErrorHint from 'client/components/dialogVerification/popupPayPoint/ErrorHint';
import DialogLoading from './DialogLoading';

// 後段 kkbox 扣點狀態
const KKBOX_EXCHANGE_STATUS = {
    INIT: 0,
    PENDING: 1,
    SUCCESS: 2,
    FAILED: 3,
    CANCELED: 4
};

function PayPointCover({ open, onClose = () => {}, onPaypointError = () => {} }) {
    const dispatch = useDispatch();
    const logout = useLogout();
    const redirectEnterpriseLogout = useLogoutRedirect();
    const { uuid: promoUUid } = useSelector(s => s.promo);
    const { enableKkpoint, kkpointAmount, transactionWithdrawQuantity } = useSelector(
        s => s.settingPromo
    );
    const { currentEntryUUID: entriesUUid } = useSelector(s => s.userEntryState);
    const loadingRef = useRef(null);
    const [loading, setLoading] = useState(true);
    const [points, setPoints] = useState(0);
    const [paidSuccess, setPaidSuccess] = useState(false);
    const [errorCode, setShowErrorCode] = useState('');
    const isKKReducePointMode = Boolean(enableKkpoint);
    const needPoints = isKKReducePointMode ? kkpointAmount : transactionWithdrawQuantity;
    const hasError = Boolean(errorCode);
    const errHandler = useClientErrorHandler();
    const [isPeddingDialogOpen, setPeddingDialogOpen] = useState(false);

    useEffect(() => {
        async function getPoint() {
            try {
                if (isKKReducePointMode) {
                    const { payload = {} } = await getKKboxUserPoints(promoUUid, entriesUUid);
                    const { data, code } = payload;
                    const { total_qty } = data ?? {};
                    if (typeof total_qty == 'number') {
                        setPoints(total_qty);
                    } else {
                        // Debug 偶發情況會回傳非數字型態格式
                        errHandler({
                            err: generateApiErrObj(payload, clientErrTypes.GET_KKBOX_USER_POINT)
                        });
                        showError(code);
                    }
                } else {
                    const resp = await getUserPoints(promoUUid, entriesUUid);
                    const { payload = {}, httpStatus } = resp;
                    if (httpStatus !== 200) {
                        throw generateApiErrObj(resp, clientErrTypes.KKBOX_API);
                    } else {
                        const { quantity } = payload;
                        setPoints(quantity);
                    }
                }
            } catch (error) {
                onPaypointError(error);
            } finally {
                setLoading(false);
            }
        }
        getPoint();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entriesUUid, promoUUid]);

    function handleUpdatePointStatus() {
        dispatch(setNeedReducePoint(false));
    }
    let isAlreadyCallKKBoxPayPoint = false;
    const KKBoxPayPoint = async () => {
        const { payload = {} } = await exchangeKKboxPoints(promoUUid, entriesUUid);
        const { data, code } = payload;
        const { status } = data || {};

        if (
            Number(status) === KKBOX_EXCHANGE_STATUS.INIT ||
            Number(status) === KKBOX_EXCHANGE_STATUS.PENDING
        ) {
            // 訂單狀態 pedding 須在呼叫一次確認狀態
            if (!isAlreadyCallKKBoxPayPoint) {
                setPeddingDialogOpen(true);
                setTimeout(async () => {
                    await KKBoxPayPoint();
                    setPeddingDialogOpen(false);
                }, 5000);
                isAlreadyCallKKBoxPayPoint = true;
            }
        } else if (Number(status) === KKBOX_EXCHANGE_STATUS.SUCCESS) {
            // 訂單狀態 - 交易成功
            setPaidSuccess(true);
            setPeddingDialogOpen(false);
        } else {
            errHandler({
                err: generateApiErrObj(payload, clientErrTypes.REDUCE_KKBOX_POINT)
            });
            showError(code);
        }
    };

    async function payPoint() {
        if (loadingRef.current) {
            return;
        }
        loadingRef.current = true;
        try {
            if (isKKReducePointMode) {
                await KKBoxPayPoint();
            } else {
                const resp = await exchangePoints(promoUUid, entriesUUid);
                const { httpStatus } = resp;
                if (httpStatus !== 200) {
                    throw generateApiErrObj(resp, clientErrTypes.KKBOX_API);
                } else {
                    setPaidSuccess(true);
                }
            }
        } catch (error) {
            onPaypointError(error);
        } finally {
            loadingRef.current = false;
        }
    }
    function showError(code) {
        setShowErrorCode(code);
    }
    function closeError() {
        setShowErrorCode('');
    }
    function logoutCloseCover() {
        logout(redirectEnterpriseLogout);
        onClose();
    }

    return (
        open &&
        !loading && (
            <div css={[maskInfoStyle, containerStyle]}>
                <div css={contentStyle}>
                    {!hasError && !paidSuccess && (
                        <PointJoin
                            currentPoints={points}
                            needPoints={needPoints}
                            onConfirm={payPoint}
                            onClose={onClose}
                            isKKReducePointMode={isKKReducePointMode}
                        />
                    )}
                    {!hasError && paidSuccess && (
                        <PointSuccess onConfirm={handleUpdatePointStatus} needPoints={needPoints} />
                    )}
                    {hasError && (
                        <ErrorHint
                            errorCode={errorCode}
                            onClose={closeError}
                            isKKReducePointMode={isKKReducePointMode}
                        />
                    )}
                    <div css={footerWrapperStyle}>
                        <Typography variant="body2" onClick={logoutCloseCover} css={logoutBtnStyle}>
                            {i18n.t('label.logout')}
                        </Typography>
                    </div>
                    <DialogLoading
                        open={isPeddingDialogOpen}
                        desc={i18n.t('desc.paying_point')}
                    ></DialogLoading>
                </div>
            </div>
        )
    );
}

export default PayPointCover;

const containerStyle = css`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 6;
`;

const maskInfoStyle = css`
    background-color: rgba(255, 255, 255, 0.72);
    backdrop-filter: blur(4px);
`;

const powerByHeight = '32px';

const contentStyle = css`
    max-width: 720px;
    height: 100%;
    min-height: calc(
        ${IS_IFRAME_MODE ? `${IFRAME_MODE_MIN_HEIGHT}px` : '100vh'} - ${powerByHeight}
    );
    padding: 32px 0;
    margin: 0 auto;
    display: flex;
    flex-direction: column;

    @media only screen and (max-width: 720px) {
        width: 100%;
        padding-left: 8px;
        padding-right: 8px;
    }
`;

const footerWrapperStyle = css`
    width: 100%;
    padding: 24px;
    text-align: right;
`;

const logoutBtnStyle = css`
    text-decoration: underline;
    cursor: pointer;
    color: #333;
`;
